import {
  GroupedOutcomeStockUnitSelectionPoolStockUnit,
  MovementArticleStockUnitSelection,
  ObservationPreviewRecord,
  ObservationPreviewUnit,
} from "types";

export type { ObservationPreviewUnit, ObservationPreviewRecord } from "types";

const EMPTY_OBSERVATION_KEY = "__empty_observation__";
type ObservationValueCarrier = {
  observation?: string | null;
};

export const buildObservationPreviewRecords = (
  previewUnits: ObservationPreviewUnit[],
): ObservationPreviewRecord[] => {
  const records = new Map<string, ObservationPreviewRecord>();

  previewUnits.forEach((previewUnit) => {
    const recordKey = previewUnit.observation ?? EMPTY_OBSERVATION_KEY;
    const existingRecord = records.get(recordKey);

    if (existingRecord) {
      existingRecord.quantity += 1;
      existingRecord.stock_unit_ids.push(previewUnit.id);
      existingRecord.history.push(...previewUnit.history);
      return;
    }

    records.set(recordKey, {
      id: `available-${previewUnit.id}`,
      title: "Observacion",
      quantity: 1,
      status: "available",
      observation: previewUnit.observation ?? null,
      history: [...previewUnit.history],
      stock_unit_ids: [previewUnit.id],
    });
  });

  return Array.from(records.values()).map((record) => ({
    ...record,
    history: [...record.history].sort(
      (left, right) =>
        new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
    ),
  }));
};

export const buildStockUnitSelection = (
  previewUnits: ObservationPreviewUnit[],
): MovementArticleStockUnitSelection => ({
  stock_unit_ids: previewUnits.map((previewUnit) => previewUnit.id),
  observation_overrides: previewUnits
    .filter(
      (previewUnit) =>
        (previewUnit.observation ?? null) !==
        (previewUnit.original_observation ?? null),
    )
    .map((previewUnit) => ({
      stock_unit_id: previewUnit.id,
      observation: previewUnit.observation ?? null,
    })),
});

export const normalizeObservationInput = (value: string): string | null => {
  const trimmedValue = value.trim();
  return trimmedValue === "" ? null : trimmedValue;
};

export const buildObservationPreviewUnitsFromSelectionPool = (
  stockUnits: GroupedOutcomeStockUnitSelectionPoolStockUnit[],
): ObservationPreviewUnit[] =>
  stockUnits.map((stockUnit) => ({
    id: stockUnit.id,
    article_location_id: stockUnit.article_location_id,
    position: stockUnit.position,
    status: "available" as const,
    observation: stockUnit.observation ?? null,
    original_observation: stockUnit.observation ?? null,
    history: [],
  }));

export const buildSelectionPoolFromPreviewUnits = (
  previewUnits: ObservationPreviewUnit[],
): GroupedOutcomeStockUnitSelectionPoolStockUnit[] =>
  previewUnits.map((previewUnit) => ({
    id: previewUnit.id,
    article_location_id: previewUnit.article_location_id,
    position: previewUnit.position,
    income_id: null,
    status: "available",
    observation: previewUnit.observation ?? null,
  }));

export const hasMultipleObservationValues = (
  stockUnits: ObservationValueCarrier[],
) =>
  new Set(
    stockUnits.map((stockUnit) => stockUnit.observation ?? EMPTY_OBSERVATION_KEY),
  ).size > 1;
